/**
 * The live room's wire format, shared by the meeting page and the live Worker
 * (`workers/live/`). The picture and the sound are RealtimeKit's
 * (notes/live-meeting-contract.md); what rides this socket is the room's group
 * chat and its reactions, as JSON text messages.
 */

import type { MomentRun } from "./live-moments";

/** A live moves forward only: scheduled, then live, then ended. */
export type LiveState = "scheduled" | "live" | "ended";

// ── reactions ───────────────────────────────────────────────────────────
/** The seven reactions a viewer can send; their faces and words are in `live-reactions.ts`. */
export const REACTION_KINDS = ["love", "agree", "clap", "idea", "funny", "party", "same"] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];
/** How many of each kind; a kind nobody sent is left out. */
export type ReactionCounts = Partial<Record<ReactionKind, number>>;

export function isReactionKind(value: unknown): value is ReactionKind {
  return typeof value === "string" && (REACTION_KINDS as readonly string[]).includes(value);
}

// ── the room's group chat ───────────────────────────────────────────────

/** The longest message the room takes. */
export const CHAT_MAX_CHARS = 500;
/** One message a second per person. */
export const CHAT_GAP_MS = 1000;
/** How many recent messages a new chat socket is sent. */
export const CHAT_HISTORY = 50;

// ── who shares a screen ─────────────────────────────────────────────────

/** The host's rule for guests' screens: they ask first, only the host shares, or anyone may. */
export const SHARE_RULES = ["ask", "host", "anyone"] as const;
export type ShareRule = (typeof SHARE_RULES)[number];

export function isShareRule(value: unknown): value is ShareRule {
  return typeof value === "string" && (SHARE_RULES as readonly string[]).includes(value);
}

/**
 * Where one guest stands with sharing: nothing asked, asking, allowed to share
 * now (the one guest who may), told not now, or another guest has the turn.
 */
export type ShareStand = "none" | "asking" | "allowed" | "declined" | "busy";

/** A guest as the host's list of asks names them. */
export interface ShareGuest {
  registrantId: string;
  name: string;
}

/** One person here, as the room's presence names them: a key of the room's own, never their id. */
export interface PresenceFace {
  key: string;
  name: string;
  /** Their own photo, the one their page holds (an asset address), when they have one. */
  photo?: string;
}

/** The most people the room's presence names; the rest are only counted. */
export const PRESENCE_FACES = 24;

/**
 * From a chat socket to the room. `pin`, `hide`, `share-rule` and `share-answer`
 * are the host's alone; `share-ask` and `share-drop` are a viewer's; `react` is anyone's, though only a viewer's is counted.
 */
export type ChatToRoom =
  | { type: "say"; body: string }
  | { type: "pin"; id: string | null }
  | { type: "hide"; id: string }
  | { type: "react"; kind: ReactionKind }
  /** Ask to share a screen; where anyone may, take the turn if nobody has it. */
  | { type: "share-ask" }
  /** Stop asking, or stop sharing. */
  | { type: "share-drop" }
  | { type: "share-rule"; rule: ShareRule }
  | { type: "share-answer"; registrantId: string; yes: boolean };

/** One message as the room shows it. */
export interface ChatLine {
  id: string;
  name: string;
  body: string;
  /** When it was said, unix seconds. */
  at: number;
  /** Said by the host. */
  host: boolean;
  /** Said by whoever this socket belongs to. */
  mine: boolean;
  /** Who said it; only the host's sockets are told, for "Reply privately". */
  registrantId?: string;
  /** A guest's photo, the newest the room knows for them (an asset address); the host's lines carry none. */
  photo?: string;
}

/** Why the room did not take what a chat socket sent. */
export type ChatRefusal = "closed" | "too-fast" | "too-long" | "empty" | "host-only";

/** From the room to a chat socket. */
export type ChatFromRoom =
  | { type: "said"; message: ChatLine }
  | { type: "pinned"; message: ChatLine | null }
  | { type: "hidden"; id: string }
  /**
   * Who is here: the count, and the newest arrivals by name and photo (at most PRESENCE_FACES). A guest's socket is
   * also told the key of their own face (`you`), so their page never seats them twice. `hostAway` is there while no
   * host socket is open and the room waits to see if the host comes back.
   */
  | { type: "presence"; here: number; faces?: PresenceFace[]; you?: string; hostAway?: true }
  | { type: "state"; state: LiveState }
  | { type: "refused"; reason: ChatRefusal }
  /** Once a second while anyone reacted: that second's totals, everyone's taps but this socket's own (they floated as they were tapped). */
  | { type: "reacted"; at: number; counts: ReactionCounts }
  /** To a guest: the host's rule, and where they stand with it. */
  | { type: "share"; rule: ShareRule; mine: ShareStand }
  /** To the host: the rule, who is asking in order, and the guest allowed to share now. */
  | { type: "share-asks"; rule: ShareRule; asks: ShareGuest[]; allowed: ShareGuest | null }
  /** What the host has done with this session's planned moments: on joining, and each time it changes. */
  | { type: "moments"; runs: MomentRun[] };

/** A chat socket's text read as something the room knows, or null. */
export function parseChatToRoom(raw: string): ChatToRoom | null {
  if (raw.length > CHAT_MAX_CHARS * 8) return null;
  let msg: { type?: unknown; body?: unknown; id?: unknown };
  try {
    msg = JSON.parse(raw) as typeof msg;
  } catch {
    return null;
  }
  if (msg.type === "say" && typeof msg.body === "string") return { type: "say", body: msg.body };
  if (msg.type === "pin" && (typeof msg.id === "string" || msg.id === null)) return { type: "pin", id: msg.id };
  if (msg.type === "hide" && typeof msg.id === "string") return { type: "hide", id: msg.id };
  const kind = (msg as { kind?: unknown }).kind;
  if (msg.type === "react" && isReactionKind(kind)) return { type: "react", kind };
  if (msg.type === "share-ask") return { type: "share-ask" };
  if (msg.type === "share-drop") return { type: "share-drop" };
  const rule = (msg as { rule?: unknown }).rule;
  if (msg.type === "share-rule" && isShareRule(rule)) return { type: "share-rule", rule };
  const answer = msg as { registrantId?: unknown; yes?: unknown };
  if (msg.type === "share-answer" && typeof answer.registrantId === "string" && typeof answer.yes === "boolean") {
    return { type: "share-answer", registrantId: answer.registrantId, yes: answer.yes };
  }
  return null;
}

const CHAT_REFUSALS: readonly ChatRefusal[] = ["closed", "too-fast", "too-long", "empty", "host-only"];
const SHARE_STANDS: readonly ShareStand[] = ["none", "asking", "allowed", "declined", "busy"];
const LIVE_STATES: readonly LiveState[] = ["scheduled", "live", "ended"];

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isText = (v: unknown): v is string => typeof v === "string";
const isLine = (v: unknown): v is ChatLine =>
  isObject(v) && isText(v.id) && isText(v.name) && isText(v.body) && typeof v.at === "number" && (v.photo === undefined || isText(v.photo));
const isFace = (v: unknown): v is PresenceFace =>
  isObject(v) && isText(v.key) && isText(v.name) && (v.photo === undefined || isText(v.photo));
const isGuest = (v: unknown): v is ShareGuest => isObject(v) && isText(v.registrantId) && isText(v.name);
const isRun = (v: unknown): v is MomentRun => isObject(v) && isText(v.interactionId) && isText(v.state);

/**
 * The room's text read as something this page knows, or null. Why: the live Worker deploys on its own, so a room
 * newer than the page can send a message the page has never heard of; that one is left unread, never drawn.
 */
export function parseChatFromRoom(raw: string): ChatFromRoom | null {
  let msg: unknown;
  try {
    msg = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(msg)) return null;
  switch (msg.type) {
    case "said":
      return isLine(msg.message) ? { type: "said", message: msg.message } : null;
    case "pinned":
      return msg.message === null || isLine(msg.message) ? { type: "pinned", message: msg.message } : null;
    case "hidden":
      return isText(msg.id) ? { type: "hidden", id: msg.id } : null;
    case "presence": {
      if (typeof msg.here !== "number" || !(msg.faces === undefined || Array.isArray(msg.faces))) return null;
      return {
        type: "presence",
        here: msg.here,
        ...(Array.isArray(msg.faces) ? { faces: msg.faces.filter(isFace) } : {}),
        ...(isText(msg.you) ? { you: msg.you } : {}),
        ...(msg.hostAway === true ? { hostAway: true as const } : {}),
      };
    }
    case "state":
      return LIVE_STATES.includes(msg.state as LiveState) ? { type: "state", state: msg.state as LiveState } : null;
    case "refused":
      return CHAT_REFUSALS.includes(msg.reason as ChatRefusal) ? { type: "refused", reason: msg.reason as ChatRefusal } : null;
    case "reacted":
      return typeof msg.at === "number" && isObject(msg.counts) ? { type: "reacted", at: msg.at, counts: msg.counts as ReactionCounts } : null;
    case "share":
      return isShareRule(msg.rule) && SHARE_STANDS.includes(msg.mine as ShareStand)
        ? { type: "share", rule: msg.rule, mine: msg.mine as ShareStand }
        : null;
    case "share-asks":
      return isShareRule(msg.rule) && Array.isArray(msg.asks) && (msg.allowed === null || isGuest(msg.allowed))
        ? { type: "share-asks", rule: msg.rule, asks: msg.asks.filter(isGuest), allowed: msg.allowed }
        : null;
    case "moments":
      return Array.isArray(msg.runs) ? { type: "moments", runs: msg.runs.filter(isRun) } : null;
    default:
      return null;
  }
}

/** How a socket is closed for a reason the person should see. */
export const LIVE_CLOSE = {
  ended: { code: 4000, reason: "The live has ended." },
  /** The studio's goodbye as it leaves: the room waits a minute for the host, not five. */
  hostLeft: { code: 4001, reason: "The host left." },
} as const;
